
import { createContext, useContext, useState, ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";
import { Sibling, Grandparent } from "@/utils/family/types";

interface FamilyFormContextType {
  motherName: string;
  setMotherName: (name: string) => void;
  fatherName: string;
  setFatherName: (name: string) => void;
  siblings: Sibling[];
  addSibling: () => void;
  updateSibling: (id: string, field: keyof Sibling, value: string) => void;
  removeSibling: (id: string) => void;
  grandparents: Grandparent[];
  addGrandparent: () => void;
  updateGrandparent: (id: string, field: keyof Grandparent, value: any) => void;
  removeGrandparent: (id: string) => void;
}

const FamilyFormContext = createContext<FamilyFormContextType | undefined>(undefined);

export function useFamilyForm() {
  const context = useContext(FamilyFormContext);
  if (!context) {
    throw new Error('useFamilyForm must be used within a FamilyFormProvider');
  }
  return context;
}

interface FamilyFormProviderProps {
  children: ReactNode | ((context: FamilyFormContextType) => ReactNode);
  initialData?: {
    motherName?: string;
    fatherName?: string;
    siblings?: Sibling[];
    grandparents?: Grandparent[];
  };
}

export function FamilyFormProvider({ children, initialData }: FamilyFormProviderProps) {
  const [motherName, setMotherName] = useState(initialData?.motherName || "");
  const [fatherName, setFatherName] = useState(initialData?.fatherName || "");
  const [siblings, setSiblings] = useState<Sibling[]>(initialData?.siblings || []);
  const [grandparents, setGrandparents] = useState<Grandparent[]>(initialData?.grandparents || []);

  const addSibling = () => {
    setSiblings([...siblings, { id: uuidv4(), name: "" }]);
  };

  const updateSibling = (id: string, field: keyof Sibling, value: string) => {
    setSiblings(siblings.map(sibling => 
      sibling.id === id ? { ...sibling, [field]: value } : sibling
    ));
  };

  const removeSibling = (id: string) => {
    setSiblings(siblings.filter(sibling => sibling.id !== id));
  };
  
  const addGrandparent = () => {
    setGrandparents([...grandparents, { id: uuidv4(), name: "", relation: 'maternal' }]);
  };
  
  const updateGrandparent = (id: string, field: keyof Grandparent, value: any) => {
    setGrandparents(grandparents.map(grandparent => 
      grandparent.id === id ? { ...grandparent, [field]: value } : grandparent
    ));
  };
  
  const removeGrandparent = (id: string) => {
    setGrandparents(grandparents.filter(grandparent => grandparent.id !== id));
  };

  const value = {
    motherName,
    setMotherName,
    fatherName,
    setFatherName,
    siblings,
    addSibling,
    updateSibling,
    removeSibling,
    grandparents,
    addGrandparent,
    updateGrandparent,
    removeGrandparent
  };

  return (
    <FamilyFormContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </FamilyFormContext.Provider>
  );
}
